import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Phone } from "lucide-react";

export const giorniDa = (iso) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null);
const colore = (g) => (g === null || g >= 60 ? "bg-red-50 text-red-700 border-red-200" : g >= 30 ? "bg-orange-50 text-orange-700 border-orange-200"
  : g >= 15 ? "bg-yellow-50 text-yellow-800 border-yellow-300" : "bg-emerald-50 text-emerald-700 border-emerald-200");
export const testoContatto = (g) => (g === null ? "Mai contattata" : g === 0 ? "Ultimo contatto: oggi" : `Ultimo contatto: ${g} ${g === 1 ? "giorno" : "giorni"} fa`);
// Prima quelle non contattate da più tempo (mai contattate in cima).
export const perContatto = (a, b) => (a.ultimo_contatto || "").localeCompare(b.ultimo_contatto || "");

export function UltimoContattoBadge({ n }) {
  const g = giorniDa(n.ultimo_contatto);
  return <span data-testid={`ultimo-contatto-${n.id}`} className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border ${colore(g)}`}>{testoContatto(g)}</span>;
}

const ESITI = [["risposto", "Risposto"], ["non_risponde", "Non risponde"], ["richiamare", "Richiamare il…"], ["non_interessato", "Non interessato"]];

export function HoChiamato({ n, onDone, testid }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btn = useRef(null);
  const apri = () => {
    if (open) return setOpen(false);
    const r = btn.current.getBoundingClientRect();
    const w = Math.min(288, window.innerWidth - 16);
    setPos({ left: Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8)), top: Math.min(r.bottom + 8, window.innerHeight - 300), width: w });
    setOpen(true);
  };
  const [f, setF] = useState({ esito: "", richiamare_il: "", nota: "" });
  const [saving, setSaving] = useState(false);
  const t = testid || `ho-chiamato-${n.id}`;
  const salva = async () => {
    if (!f.esito) return toast.error("Scegli l'esito della chiamata");
    if (f.esito === "richiamare" && !f.richiamare_il) return toast.error("Scegli la data in cui richiamare");
    setSaving(true);
    try {
      const { data } = await api.post(`/notizie/${n.id}/contatto`, f);
      toast.success(data.task_id ? "Contatto registrato e task di richiamo creato" : "Contatto registrato");
      setOpen(false); setF({ esito: "", richiamare_il: "", nota: "" });
      onDone && onDone(data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <span className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button ref={btn} type="button" data-testid={t} onClick={apri} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-[#2B2B2B] text-white hover:bg-black"><Phone size={12} /> Ho chiamato</button>
      {open && pos && createPortal(<>
        <div className="fixed inset-0 z-[140]" onClick={() => setOpen(false)} />
        <div data-testid={`${t}-box`} style={{ left: pos.left, top: Math.max(8, pos.top), width: pos.width }} className="fixed z-[141] rounded-2xl border border-[var(--border)] bg-white shadow-xl p-3 space-y-2 text-left" onClick={(e) => e.stopPropagation()}>
          <div className="grid grid-cols-2 gap-1.5">{ESITI.map(([k, l]) => (
            <button key={k} type="button" data-testid={`${t}-esito-${k}`} onClick={() => setF({ ...f, esito: k })}
              className={`px-2 py-1.5 rounded-lg text-xs font-semibold border ${f.esito === k ? "bg-[#FC82ED] border-[#FC82ED] text-[#2B2B2B]" : "border-[var(--border)]"}`}>{l}</button>))}</div>
          {f.esito === "richiamare" && <input type="date" data-testid={`${t}-data`} value={f.richiamare_il} onChange={(e) => setF({ ...f, richiamare_il: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-base sm:text-sm" />}
          <textarea data-testid={`${t}-nota`} rows={2} value={f.nota} onChange={(e) => setF({ ...f, nota: e.target.value })} placeholder="Nota (facoltativa)" className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-base sm:text-sm" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs font-semibold rounded-lg border">Annulla</button>
            <button type="button" data-testid={`${t}-salva`} disabled={saving} onClick={salva} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#2B2B2B] text-white disabled:opacity-50">Salva</button>
          </div>
        </div></>, document.body)}
    </span>
  );
}

export const daRichiamare = (list, giorni) => list.filter((n) => { const g = giorniDa(n.ultimo_contatto); return g === null || g >= giorni; }).sort(perContatto);

const TEMP = { calda: "Calda", media: "Media", fredda: "Fredda" };

export function DaRichiamare({ items, agenti, conFiltroAgente, onDone }) {
  const nav = useNavigate();
  const [giorni, setGiorni] = useState(90);
  const [ag, setAg] = useState("all");
  const lista = daRichiamare(items.filter((n) => ag === "all" || n.owner_agent_id === ag), giorni);
  return (
    <div className="card-immo p-4 sm:p-5 mb-4" data-testid="da-richiamare-panel">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)]">Non contattate da almeno</span>
        {[30, 60, 90, 180].map((g) => <button key={g} data-testid={`richiamare-giorni-${g}`} onClick={() => setGiorni(g)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${giorni === g ? "bg-[#2B2B2B] text-white border-[#2B2B2B]" : "border-[var(--border)]"}`}>{g} giorni</button>)}
        {conFiltroAgente && <select data-testid="richiamare-agente" value={ag} onChange={(e) => setAg(e.target.value)} className="ml-auto px-3 py-1.5 rounded-xl border border-[var(--border)] text-sm bg-white">
          <option value="all">Tutti gli agenti</option>{agenti.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}</select>}
      </div>
      {!lista.length ? <p className="text-sm text-[var(--muted)]" data-testid="richiamare-vuoto">Nessuna notizia da richiamare.</p> : (
        <div className="divide-y divide-[var(--border)]">{lista.map((n) => {
          const g = giorniDa(n.ultimo_contatto);
          return (
            <div key={n.id} data-testid={`richiamare-row-${n.id}`} className="py-3 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="text-sm font-semibold">{[n.nome, n.cognome].filter(Boolean).join(" ") || "Proprietario da individuare"}</div>
                <div className="text-xs text-[var(--muted)]">{[n.via_immobile || n.indirizzo, n.comune].filter(Boolean).join(", ")} · {TEMP[n.temperatura] || "Senza temp."} · Agente: {n.owner_agent_name || "—"}</div>
              </div>
              <span data-testid={`richiamare-giorni-row-${n.id}`} className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${colore(g)}`}>{g === null ? "Mai contattata" : `${g} giorni`}</span>
              <HoChiamato n={n} onDone={onDone} testid={`richiamare-chiamato-${n.id}`} />
              <button data-testid={`richiamare-apri-${n.id}`} onClick={() => nav(`/notizie/${n.id}`)} className="px-3 py-1.5 rounded-full text-xs font-bold border border-[var(--border)] hover:border-[#FC82ED]">Apri</button>
            </div>);
        })}</div>)}
    </div>
  );
}
